import React from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'

import { selectFullscreen, setFullScreen } from '@store/actionSlices/appConfig'
import {
  BuildingInterface,
  setBuilding,
  setByFlag,
} from '@store/actionSlices/building'
import { setInteractivePlan } from '@store/actionSlices/interactivePlan'
import { setPanoramicData } from '@store/actionSlices/panoramic'
import { ShortlistState } from '@store/actionSlices/shortlist'
import { setFilter } from '@store/actionSlices/unitFilter'
import {
  Channel,
  ExtendedUnitFilterOptions,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
} from '@store/types'

import Container from '@components/container'
import DataHandler from '@components/data-handler'
import {
  FULFILLED_STATUS,
  PENDING_STATUS,
} from '@components/data-handler/data-handler'
import FilterPopup from '@components/filter-popup'
import { useIdleTimeHandler } from '@components/idle-time-handler/utils'
import { PropertyCompareTransitionWrapper } from '@components/properties-view'
import { PannellumDataInterface } from '@components/showcase-pannellum/types'

import { useGetBuildingQuerySilent } from '@api/building'
import {
  MappingBlockCollection,
  MappingCollection,
  selectFromResult as selectFromResultInteractive,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'
import {
  selectFromResult as selectFromPanoramicResult,
  useGetPanoramicQuery,
} from '@api/panoramic'

import getFilteredBuildingData from '@utilities/filter-building'
import { getQueryStringParams } from '@utilities/helper'
import LightMapHandler from '@utilities/lightmap-handler/lightmap-handler'

import {
  useCompareProperty,
  useSyncComparablePropertiesFromSession,
} from '@adUtilities/components/compare-property/utils'
import { PropertyType } from '@adUtilities/types'
import { Unit } from '@adUtilities/types/apartment'

import FloorplateView from './floorplate-view'
import BuildingSkeleton from './skeleton/building-skeleton'
import UnitView from './unit-view'
import UnitsSidePanel from './units-side-panel'

interface BuildingProps {
  building: BuildingInterface
  floorplan: MappingCollection
  blocks: MappingBlockCollection
  shortlists: ShortlistState['shortlists']
  panoramic: Array<PannellumDataInterface>
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  channels: Array<Channel>
  unitFilter: ExtendedUnitFilterOptions
  showPrice: boolean
  lightUpBuildingModelOnFilter: boolean
  availableStatusLabel: string
  onFullScreenToggle?: React.Dispatch<React.SetStateAction<boolean | undefined>>
  controls: {
    active: boolean
    duration: number
  }
}

function Building({
  building,
  floorplan,
  blocks,
  shortlists,
  panoramic,
  session,
  projectIdentity,
  channels,
  unitFilter,
  showPrice,
  lightUpBuildingModelOnFilter,
  availableStatusLabel,
  onFullScreenToggle,
  controls,
}: BuildingProps) {
  const fullscreen = useSelector(selectFullscreen)
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const urlParams = React.useRef(getQueryStringParams(location.search))
  const floorplanLength = React.useMemo(
    () => Object.keys(floorplan).length,
    [floorplan]
  )

  const [unit, setUnit] = React.useState<Unit>()
  const [unitSidePanelState, setUnitSidePanelState] = React.useState(true)
  const [propertyInfoToggle, setPropertyInfoToggle] = React.useState(
    !!session?.building?.unitInfoPopup
  )
  const [isFilterOpen, toggleFilter] = React.useState(false)
  const isConnected = React.useMemo(
    () => session?.connected || false,
    [session]
  )

  const isAppIdle = useIdleTimeHandler({
    isConnected,
    timeoutInSeconds: controls?.duration || 3,
    isIdleActive: controls?.active || false,
  })

  const interactivePayload = useGetInteractivePlanQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromResultInteractive }
  )

  const buildingPayload = useGetBuildingQuerySilent({
    projectName: projectIdentity.projectName,
  })

  const panoramicPayload = useGetPanoramicQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromPanoramicResult }
  )

  const blockList: Array<string> = React.useMemo(
    () =>
      building?.blockOrders.length >= 1
        ? building?.blockOrders
        : Object.keys(blocks) || [],
    [building?.blockOrders, blocks]
  )

  const filteredItems = React.useMemo(() => {
    const output: Array<{ name: string; type: string }> = []
    const data = getFilteredBuildingData(
      building,
      unitFilter,
      showPrice,
      blockList.length,
      availableStatusLabel
    )
    if (data.length > 0) {
      data.forEach((myLevel) => {
        if (myLevel.data.length > 0) {
          myLevel.data.forEach((myUnit) => {
            output.push({
              name: myUnit.name,
              type: 'unit',
            })
          })
        }
      })
    }
    return output
  }, [unitFilter.apply])

  LightMapHandler({
    isActive: true,
    projectIdentity,
    channels: Object.values(channels),
    activeLevel: building.activeLevel,
    activeUnit: building.activeUnit,
    activeBlock: building.blockOrders?.length > 1 ? building.activeBlock : null,
    shortlists,
    lightUpBuildingModelOnFilter,
    filteredApplied: unitFilter.apply,
    filteredItems: unitFilter.apply ? filteredItems : [],
  })

  const handleActiveBlockControl = (blockCntrl: string) => {
    if (blockCntrl !== building.activeBlock) {
      dispatch(setByFlag({ flag: 'activeBlock', value: blockCntrl }))
    }
  }

  const handleActiveLevelControl = (activeLevel: string) => {
    if (activeLevel !== building.activeLevel) {
      dispatch(setByFlag({ flag: 'activeLevel', value: activeLevel }))
    }
  }

  const handleActiveUnitControl = (activeUnit: string) => {
    setTimeout(
      () => {
        dispatch(setByFlag({ flag: 'activeUnit', value: activeUnit }))
      },
      activeUnit ? 1000 : 0
    )
  }

  const handleFullScreenToggle = (arg: boolean) => {
    dispatch(setFullScreen(arg))
    setUnitSidePanelState(!arg)
    if (!isConnected) onFullScreenToggle?.(arg)
  }

  const findUnit = () => {
    building.levels.forEach((lvl) => {
      const foundUnit = lvl.data.find(
        (unt) =>
          unt.name.replace(/\s/g, '') === building.activeUnit.replace(/\s/g, '')
      )
      if (foundUnit) {
        setUnit(foundUnit)
      }
    })
  }

  const resetUnit = () => {
    setUnit(undefined)
  }

  React.useEffect(() => {
    if (building.levels.length && building.activeUnit) {
      findUnit()
    }
    if (!building.activeUnit) {
      resetUnit()
    }
  }, [building])

  React.useEffect(() => {
    const { maps } = interactivePayload
    if (!floorplanLength && Object.keys(maps.floorplan || {}).length) {
      dispatch(setInteractivePlan(maps))
    }
  }, [interactivePayload, floorplan])

  const prevBuildingStatusRef = React.useRef<string>()
  const lastDispatchedTimestamp = React.useRef<number>()

  const comparePropertyProps = useCompareProperty()

  useSyncComparablePropertiesFromSession({
    disable: !session?.connected,
    firebasePropertyCompare: session?.propertyCompare,
    propertyType: PropertyType.Unit,
    dataLoaded: buildingPayload.status === FULFILLED_STATUS,
    data: buildingPayload.building,
    comparePropertyProps,
  })

  React.useEffect(() => {
    const {
      building: buildingData,
      blockOrders: blockOrdersData,
      status,
    } = buildingPayload

    if (
      prevBuildingStatusRef.current === PENDING_STATUS &&
      status === FULFILLED_STATUS &&
      building.levels.length > 0 &&
      buildingData.length > 0
    ) {
      dispatch(
        setBuilding({
          ...building,
          levels: buildingData,
          blockOrders: blockOrdersData,
        })
      )

      if (!session?.connected && comparePropertyProps.compareModeIsActive) {
        const refreshed = comparePropertyProps.comparableProperties.map(
          (comparable) => {
            const { property } = comparable
            if (!property || property.propertyType !== PropertyType.Unit) {
              return comparable
            }
            const matchedLevel = buildingData.find(
              (level) => level.level === property.references.levelId
            )
            const matchedUnit = matchedLevel?.data.find(
              (unit) => unit.id === property.data.id
            )
            return {
              ...comparable,
              property: matchedUnit
                ? { ...property, data: matchedUnit }
                : property,
            }
          }
        )
        comparePropertyProps.setComparableProperties(refreshed)
      }
    }
    prevBuildingStatusRef.current = status
  }, [buildingPayload.status])

  React.useEffect(() => {
    const { building: buildingData, blockOrders: blockOrdersData } =
      buildingPayload

    if (building.levels.length === 0 && buildingData.length > 0) {
      const blockKeys = Object.keys(blocks)
      let orderedBlockKeys = blockKeys.length > 1 ? blockKeys : []
      if (blockOrdersData.length > 1) {
        orderedBlockKeys = blockOrdersData
      }
      const activeBlock =
        building.activeBlock ||
        urlParams.current?.block ||
        orderedBlockKeys[0] ||
        buildingData[0]?.data[0].blockId ||
        ''

      let activeLevel =
        building.activeLevel ||
        urlParams.current?.level ||
        buildingData[0]?.level

      if (!urlParams.current?.level && blockOrdersData.length > 1) {
        const firstLevel = buildingData.find((lvl) =>
          lvl.data.some((unit) => unit.blockId === activeBlock)
        )
        activeLevel = firstLevel?.level || buildingData[0]?.level
      }

      const activeUnit = building.activeUnit || urlParams.current?.unit

      dispatch(
        setBuilding({
          ...building,
          levels: buildingData,
          blockOrders: blockOrdersData,
          activeBlock,
          activeLevel,
          activeUnit,
        })
      )
    }

    if (
      buildingData.length > 0 &&
      buildingPayload.fulfilledTimeStamp !== lastDispatchedTimestamp.current
    ) {
      lastDispatchedTimestamp.current = buildingPayload.fulfilledTimeStamp
      dispatch(
        setBuilding({
          ...building,
          levels: buildingData,
          blockOrders: blockOrdersData,
        })
      )
    }
  }, [interactivePayload, buildingPayload, floorplanLength, building, blocks])

  React.useEffect(() => {
    const { panoramic: panoramicData, isLoaded } = panoramicPayload
    if (panoramic.length === 0 && isLoaded && panoramicData.length > 0) {
      dispatch(setPanoramicData(panoramicData))
    }
  }, [panoramicPayload])

  React.useEffect(() => {
    if (session) {
      const {
        connected,
        building: {
          activeBlock,
          activeLevel,
          activeUnit,
          filterPopup,
          unitFilter: unitFilterFirestore,
          unitInfoPopup,
        },
      } = session
      if (connected) {
        dispatch(setFilter(unitFilterFirestore))
        toggleFilter(filterPopup)
        handleActiveBlockControl(activeBlock)
        handleActiveUnitControl(activeUnit)
        handleActiveLevelControl(activeLevel)
        setPropertyInfoToggle(unitInfoPopup)
      }
    }
  }, [session])

  React.useEffect(
    () => () => onFullScreenToggle?.(undefined),
    [onFullScreenToggle]
  )

  React.useEffect(() => {
    if (
      !(
        urlParams.current?.block ||
        urlParams.current?.level ||
        urlParams.current?.unit
      )
    ) {
      return
    }
    history.replace(location.pathname)
  }, [location.pathname])

  React.useEffect(() => {
    if (session?.connected) {
      return undefined
    }
    comparePropertyProps.stopComparison()
    return () => comparePropertyProps.stopComparison()
  }, [session?.connected])

  return (
    <Container fullScreenMode={fullscreen || isAppIdle} className="flex">
      <DataHandler
        payloads={{
          ...buildingPayload,
          data: building.levels,
        }}
        skeletonFrame={
          <BuildingSkeleton fullscreen={fullscreen || isConnected} />
        }
        className="flex"
      >
        <>
          {!isConnected && (
            <div
              className={`h-full ${
                isAppIdle || !unitSidePanelState
                  ? '!w-0 opacity-0'
                  : 'opacity-100'
              } w-[297px] flex-grow-0 transition-all duration-300 ease-in-out`}
            >
              <UnitsSidePanel
                filterPopupToggle={isFilterOpen}
                setFilterPopupToggle={(arg: boolean) => toggleFilter(arg)}
                comparePropertyProps={comparePropertyProps}
              />

              <FilterPopup isOpen={isFilterOpen} toggle={toggleFilter} />
            </div>
          )}

          <PropertyCompareTransitionWrapper
            comparePropertyProps={comparePropertyProps}
            showInfo={propertyInfoToggle}
            onFullScreenToggle={handleFullScreenToggle}
            onPropertyInfoToggle={setPropertyInfoToggle}
            isConnected={!!session?.connected}
          >
            <div
              className={`z-10 h-full flex-1 ${
                building.activeUnit
                  ? 'transition-all duration-300 ease-in-out'
                  : ''
              }`}
            >
              {building.activeUnit && unit && (
                <UnitView
                  key={unit.id}
                  unit={unit}
                  setFullScreenToggle={handleFullScreenToggle}
                  getVideoPlayerState={handleFullScreenToggle}
                  comparePropertyProps={comparePropertyProps}
                  unitInfoToggle={propertyInfoToggle}
                  setUnitInfoToggle={setPropertyInfoToggle}
                />
              )}

              {building.activeLevel && !building.activeUnit && !unit && (
                <DataHandler
                  payloads={{
                    ...interactivePayload,
                    data: floorplanLength,
                  }}
                  skeletonFrame={
                    <BuildingSkeleton fullscreen={fullscreen || isConnected} />
                  }
                  className="relative"
                >
                  <FloorplateView
                    getVideoPlayerState={handleFullScreenToggle}
                    setFullScreenToggle={handleFullScreenToggle}
                  />
                </DataHandler>
              )}
            </div>
          </PropertyCompareTransitionWrapper>
        </>
      </DataHandler>
    </Container>
  )
}

export default connect(
  ({
    interactivePlan: { blocks, floorplan },
    projectIdentity,
    shortlist: { shortlists },
    panoramic,
    firestore: { session, lightMap },
    building,
    unitFilter,
    projectConfig: {
      showPrice,
      lightUpBuildingModelOnFilter,
      statusLabels,
      propertyGalleryTab,
    },
  }: RootStateTypeExtra) => ({
    floorplan,
    blocks,
    projectIdentity,
    building,
    shortlists,
    panoramic,
    unitFilter,
    showPrice,
    lightUpBuildingModelOnFilter,
    session,
    channels: lightMap?.channels || [],
    availableStatusLabel: statusLabels?.available,
    controls: propertyGalleryTab,
  })
)(Building)
